// App-shell badge updates: pushes the aggregate unread count to the
// browser tab title (web + Electron) and the Electron dock/taskbar badge.
// Mounted once near the App root so it owns these two side-effects in a
// single place — without this hook, the OS-level signals stay frozen even
// though the in-app sidebar updates correctly.

import { useEffect, useRef } from "react";
import { useGlobalUnreadCounts } from "@/lib/selectors/global-unread-counts";
import { addSentryBreadcrumb } from "@/lib/sentry";

const BASE_TITLE = "Ano";

interface ElectronBadgeApi {
  setBadgeCount?: (count: number) => Promise<unknown>;
}

function getElectronBadgeApi(): ElectronBadgeApi | null {
  if (typeof window === "undefined") return null;
  const api = (window as unknown as { __electronApi?: ElectronBadgeApi })
    .__electronApi;
  return api ?? null;
}

export function useChromeBadge(selectedChannel: string | null): void {
  const counts = useGlobalUnreadCounts(selectedChannel);
  const sentMountedBreadcrumbRef = useRef(false);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.title =
      counts.totalUnread > 0
        ? `(${counts.totalUnread}) ${BASE_TITLE}`
        : BASE_TITLE;
  }, [counts.totalUnread]);

  useEffect(() => {
    const api = getElectronBadgeApi();
    if (!sentMountedBreadcrumbRef.current) {
      sentMountedBreadcrumbRef.current = true;
      addSentryBreadcrumb({
        category: "badge",
        message: "useChromeBadge mounted",
        data: {
          hasElectronApi:
            typeof window !== "undefined" &&
            !!(window as unknown as { __electronApi?: unknown }).__electronApi,
          hasSetBadgeCount: !!api?.setBadgeCount,
          totalUnread: counts.totalUnread,
          totalMentions: counts.totalMentions,
        },
      });
    }
    if (!api?.setBadgeCount) return;
    // Mac dock / Linux taskbar: pass mention count when present (more
    // signal-rich than total unread), otherwise fall back to total unread.
    // Pass 0 explicitly to clear the badge — main process handles the
    // platform-specific empty-string conversion.
    const value =
      counts.totalMentions > 0 ? counts.totalMentions : counts.totalUnread;
    void api.setBadgeCount(value).catch(() => {
      // Swallow IPC errors; badge updates are best-effort UX, not load-bearing.
    });
  }, [counts.totalMentions, counts.totalUnread]);
}
